/**
 * Auth API client — wraps `/api/auth/*`.
 *
 * Endpoints (see api/src/routes/auth.js):
 *   POST /api/auth/register
 *   POST /api/auth/login
 *   POST /api/auth/refresh
 *   POST /api/auth/logout
 *
 * The bundle shape is documented in api/src/services/auth.js
 * (`buildTokenBundle`). The api client already unwraps the REST-SPEC
 * envelope's `data` field, so callers receive the bundle directly.
 *
 * Token persistence is delegated to `authStore`: callers should pass the
 * returned bundle to `useAuthStore.getState().signIn(bundle)` after a
 * successful register/login/refresh so localStorage stays in sync.
 */

import { api } from './api';
import { useAuthStore } from '../stores/authStore';

/** Bearer header for the authenticated auth endpoints (resend-verification). */
function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export interface AuthUser {
  id: string;
  email: string;
  role: string;
  /** Email-verification state (#209). Informational — never gates auth. */
  emailVerified?: boolean;
}

export interface AuthBundle {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: AuthUser;
}

export interface RegisterInput {
  email: string;
  password: string;
}

export interface LoginInput {
  email: string;
  password: string;
}

/** POST /api/auth/register */
export function register(input: RegisterInput): Promise<AuthBundle> {
  return api.post<AuthBundle>('/auth/register', input);
}

/** POST /api/auth/login */
export function login(input: LoginInput): Promise<AuthBundle> {
  return api.post<AuthBundle>('/auth/login', input);
}

/** POST /api/auth/refresh */
export function refresh(refreshToken: string): Promise<AuthBundle> {
  return api.post<AuthBundle>('/auth/refresh', { refreshToken });
}

/** POST /api/auth/logout */
export function logout(): Promise<{ loggedOut: boolean }> {
  return api.post<{ loggedOut: boolean }>('/auth/logout');
}

/**
 * POST /api/auth/forgot-password — begin a password reset (#122). Always
 * resolves with a generic message regardless of whether the email exists.
 */
export function forgotPassword(email: string): Promise<{ message: string }> {
  return api.post<{ message: string }>('/auth/forgot-password', { email });
}

/** POST /api/auth/reset-password — complete a reset with a token + new password (#122). */
export function resetPassword(token: string, password: string): Promise<{ message: string }> {
  return api.post<{ message: string }>('/auth/reset-password', { token, password });
}

/** POST /api/auth/verify-email — confirm an email with a token from the link (#209). */
export function verifyEmail(token: string): Promise<{ message: string }> {
  return api.post<{ message: string }>('/auth/verify-email', { token });
}

/**
 * POST /api/auth/resend-verification — re-send the signed-in user's verification
 * email (#209). Authenticated (Bearer). Always resolves with a generic message
 * (a no-op if the address is already verified).
 */
export function resendVerification(): Promise<{ message: string }> {
  return api.post<{ message: string }>('/auth/resend-verification', undefined, {
    headers: authHeaders(),
  });
}

export const authApi = {
  register,
  login,
  refresh,
  logout,
  forgotPassword,
  resetPassword,
  verifyEmail,
  resendVerification,
};

export default authApi;
